import { NACE_COMPETENCIES } from 'constants/nace-competencies';
import React, { ReactElement, useEffect, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useCoCurricularApi } from 'services/api/hooks';
import { colorGrey } from 'customColors';
import { theme } from 'theme';

interface StatItemProps {
  label: string;
  value: string;
  isZero: boolean;
}

const StatItem = ({ label, value, isZero }: StatItemProps): ReactElement => {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '12px',
          color: '#6B6B6B',
          marginBottom: '4px',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '32px',
          fontWeight: 700,
          color: isZero ? colorGrey : '#1F1F1F',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export const StatsSummary = (): ReactElement => {
  const coCurricularApi = useCoCurricularApi();
  const [totalHours, setTotalHours] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [totalLeadershipPositions, setTotalLeadershipPositions] = useState(0);
  const [naceCompetenciesEarned, setNaceCompetenciesEarned] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      const result = await coCurricularApi.getSummary();

      if (!cancelled && result.success) {
        setTotalHours(result.value.totalHours);
        setTotalEvents(result.value.totalEvents);
        setTotalLeadershipPositions(result.value.totalLeadershipPositions);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [coCurricularApi]);

  useEffect(() => {
    let cancelled = false;

    const fetchNace = async () => {
      const result = await coCurricularApi.getNaceCompetencyHours();

      if (!cancelled && result.success) {
        const earned = result.value.naceCompetencies.filter(nc => nc.eventCount > 0).length;
        setNaceCompetenciesEarned(earned);
      }
    };

    fetchNace();

    return () => {
      cancelled = true;
    };
  }, [coCurricularApi]);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      sx={{
        marginBottom: '48px',
        border: '1px solid #E8E8E8',
      }}
    >
      <Box
        sx={{
          flex: 1,
          padding: '16px',
          borderRight: isMobile ? 'none' : '1px solid #E8E8E8',
          borderBottom: isMobile ? '1px solid #E8E8E8' : 'none',
        }}
      >
        <StatItem label="Hours of Involvement" value={String(totalHours)} isZero={totalHours === 0} />
      </Box>
      <Box
        sx={{
          flex: 1,
          padding: '16px',
          borderRight: isMobile ? 'none' : '1px solid #E8E8E8',
          borderBottom: isMobile ? '1px solid #E8E8E8' : 'none',
        }}
      >
        <StatItem label="Events Attended" value={String(totalEvents)} isZero={totalEvents === 0} />
      </Box>
      <Box
        sx={{
          flex: 1,
          padding: '16px',
          borderRight: isMobile ? 'none' : '1px solid #E8E8E8',
          borderBottom: isMobile ? '1px solid #E8E8E8' : 'none',
        }}
      >
        <StatItem
          label="NACE Competencies Earned"
          value={`${naceCompetenciesEarned} of ${NACE_COMPETENCIES.length}`}
          isZero={naceCompetenciesEarned === 0}
        />
      </Box>
      <Box sx={{ flex: 1, padding: '16px' }}>
        <StatItem
          label="Leadership Positions"
          value={String(totalLeadershipPositions)}
          isZero={totalLeadershipPositions === 0}
        />
      </Box>
    </Box>
  );
};
